import type {
  CreateBudgetCategoryInput,
  CreateBudgetExpenseInput,
  CreateBudgetPaymentInput,
  UpdateBudgetCategoryInput,
  UpdateBudgetExpenseInput,
  UpdateBudgetPaymentInput,
  UpsertBudgetPlanInput,
} from '@amore/shared'
import { queryOptions } from '@tanstack/react-query'
import type { InferResponseType } from 'hono/client'
import { api, unwrap, unwrapVoid } from '@/lib/api'

const workspaceApi = api.api.workspaces[':slug']
export type BudgetResponse = InferResponseType<typeof workspaceApi.budget.$get, 200>

export const budgetKeys = {
  all: (slug: string) => ['budget', slug] as const,
}

export const budgetQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: budgetKeys.all(slug),
    queryFn: () => unwrap<BudgetResponse>(workspaceApi.budget.$get({ param: { slug } })),
  })

export function upsertBudgetPlanRequest(slug: string, json: UpsertBudgetPlanInput) {
  return unwrap<BudgetResponse>(workspaceApi.budget.$put({ param: { slug }, json }))
}

export function createBudgetCategoryRequest(slug: string, json: CreateBudgetCategoryInput) {
  return unwrap(
    workspaceApi['budget-categories'].$post({
      param: { slug },
      json,
    }),
  )
}

export function updateBudgetCategoryRequest(
  slug: string,
  categoryId: string,
  json: UpdateBudgetCategoryInput,
) {
  return unwrap(
    workspaceApi['budget-categories'][':categoryId'].$patch({
      param: { slug, categoryId },
      json,
    }),
  )
}

export function deleteBudgetCategoryRequest(slug: string, categoryId: string) {
  return unwrapVoid(
    workspaceApi['budget-categories'][':categoryId'].$delete({
      param: { slug, categoryId },
    }),
  )
}

export function createBudgetExpenseRequest(
  slug: string,
  categoryId: string,
  json: CreateBudgetExpenseInput,
) {
  return unwrap(
    workspaceApi['budget-categories'][':categoryId'].expenses.$post({
      param: { slug, categoryId },
      json,
    }),
  )
}

export function updateBudgetExpenseRequest(
  slug: string,
  expenseId: string,
  json: UpdateBudgetExpenseInput,
) {
  return unwrap(
    workspaceApi['budget-expenses'][':expenseId'].$patch({
      param: { slug, expenseId },
      json,
    }),
  )
}

export function deleteBudgetExpenseRequest(slug: string, expenseId: string) {
  return unwrapVoid(
    workspaceApi['budget-expenses'][':expenseId'].$delete({
      param: { slug, expenseId },
    }),
  )
}

export function createBudgetPaymentRequest(
  slug: string,
  expenseId: string,
  json: CreateBudgetPaymentInput,
) {
  return unwrap(
    workspaceApi['budget-expenses'][':expenseId'].payments.$post({
      param: { slug, expenseId },
      json,
    }),
  )
}

export function updateBudgetPaymentRequest(
  slug: string,
  paymentId: string,
  json: UpdateBudgetPaymentInput,
) {
  return unwrap(
    workspaceApi['budget-payments'][':paymentId'].$patch({
      param: { slug, paymentId },
      json,
    }),
  )
}

export function deleteBudgetPaymentRequest(slug: string, paymentId: string) {
  return unwrapVoid(
    workspaceApi['budget-payments'][':paymentId'].$delete({
      param: { slug, paymentId },
    }),
  )
}
